import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, MapPinned } from 'lucide-react';
import useJizdaPlayback from '../../hooks/useJizdaPlayback';
import VozidloDlazdice from '../flotila/VozidloDlazdice';
import VozidloKartaLive from '../flotila/VozidloKartaLive';
import MapVozidla from '../flotila/MapVozidla';
import Inspector from '../flotila/Inspector';
import SidePanel from '../SidePanel';
import { SECTION_COLOR } from './ui';

/**
 * Rozvoz → Živě. Mapa aut, rozvozů na cestě a objednávek v přípravě; zóny
 * jako tichá vrstva pod auty. Mapa je pracovní plocha, ne seznam — proto
 * nejede na `ModulLayout` (přiznaná výjimka normy). Inspektor auta ukazuje
 * jen živý stav a knihu jízd, agenda auta (tankování, servis, pojištění,
 * nastavení) je v záložce Auta. Inspektor mapu překryje, neodsouvá ji (§1f).
 */
export default function RozvozZive({ vozidla, ridiciMap, zony = [], onOtevritAuto, cardStyle, isDark }) {
    const [vybraneId, setVybraneId] = useState(null);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    // Vybraná historická jízda z knihy jízd — patří konkrétnímu autu,
    // při změně výběru se zahodí. Playback sdílí inspektor i mapa.
    const [selectedJizdaId, setSelectedJizdaId] = useState(null);
    useEffect(() => { setSelectedJizdaId(null); }, [vybraneId]);
    const playback = useJizdaPlayback(selectedJizdaId);
    const [ukazatZony, setUkazatZony] = useState(() => {
        try { return localStorage.getItem('rozvoz.zive.zony') !== '0'; } catch { return true; }
    });
    useEffect(() => { try { localStorage.setItem('rozvoz.zive.zony', ukazatZony ? '1' : '0'); } catch {} }, [ukazatZony]);

    // Horní hrana mapy = horní hrana dat pro inspektor (norma §1f)
    const mapaRef = useRef(null);
    const [inspectorTopOffset, setInspectorTopOffset] = useState(0);
    useEffect(() => {
        const zmer = () => { const el = mapaRef.current; if (el) setInspectorTopOffset(Math.max(0, Math.round(el.getBoundingClientRect().top))); };
        zmer(); window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [inspectorOpen]);
    const vybrane = vozidla.find((v) => v.id === vybraneId) || null;
    const ridicVybr = vybrane?.aktualni_ridic_id ? ridiciMap.get(vybrane.aktualni_ridic_id) : null;

    // Fullscreen overlay — kontejner se přepne na position:fixed inset:0 a roztáhne
    // se přes celé okno prohlížeče (ne nativní requestFullscreen, jen layout overlay).
    // Esc i opětovný klik na tlačítko stav vrátí.
    const [isFullscreen, setIsFullscreen] = useState(false);
    useEffect(() => {
        if (!isFullscreen) return;
        const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
        window.addEventListener('keydown', onKey);
        // Skryj scroll body, aby pod overlayem nešlo náhodou scrollovat.
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [isFullscreen]);
    // Defenzivní reset overflow při unmountu (přepnutí Tab pryč v fullscreenu) —
    // bez toho by zůstal overflow:hidden na body a uživatel by nemohl scrollovat.
    useEffect(() => () => {
        document.body.style.overflow = '';
    }, []);

    // Sdílený state aktivních rozvozů — používá se v dlaždici (mini-karty objednávek)
    // i v MapVozidla (markery + polylines). Polling 20s — Reverb pro objednávky není.
    const [rozvozy, setRozvozy] = useState([]);
    useEffect(() => {
        let cancelled = false;
        const fetchOnce = () => {
            fetch('/api/flotila/aktivni-rozvozy')
                .then((r) => r.json())
                .then((d) => { if (!cancelled && Array.isArray(d)) setRozvozy(d); })
                .catch(() => {});
        };
        fetchOnce();
        const i = setInterval(fetchOnce, 20_000);
        return () => { cancelled = true; clearInterval(i); };
    }, []);
    // rozvozMap se buduje až z filtrovaných aktivních rozvozů níže — bez
    // toho by VozidloDlazdice tahaly stale `rozvoz.rozvozce_jmeno` po
    // klock-outu řidiče.
    // (definice přesunuta níže za aktivniRozvozy)

    // Objednávky ve fázi přípravy (kuchyň vaří / fronta čeká). Šedé markery
    // na mapě, ať dispečer vidí budoucí cíle rozvozu. Polling 15 s — rychleji
    // než rozvozy, protože tento stav se mění (přijatá → v dávce → na cestě).
    const [pripravuje, setPripravuje] = useState([]);
    useEffect(() => {
        let cancelled = false;
        const fetchOnce = () => {
            fetch('/api/flotila/objednavky-pripravuje')
                .then((r) => r.json())
                .then((d) => { if (!cancelled && Array.isArray(d)) setPripravuje(d); })
                .catch(() => {});
        };
        fetchOnce();
        const i = setInterval(fetchOnce, 15_000);
        return () => { cancelled = true; clearInterval(i); };
    }, []);

    // Skóre řidičů ze směny + 30-day stabilní známky (znamka_abs 1-5). Pomalý
    // poll 30s — scoring se nemění každou sekundu. Response shape:
    //   { driverId: { shift_*, long_*, znamka_abs } }
    const [ridiciSkore, setRidiciSkore] = useState({});
    useEffect(() => {
        let cancelled = false;
        const fetchOnce = () => {
            fetch('/api/flotila/ridici-smene')
                .then((r) => r.json())
                .then((d) => { if (!cancelled && d && typeof d === 'object') setRidiciSkore(d); })
                .catch(() => {});
        };
        fetchOnce();
        const i = setInterval(fetchOnce, 30_000);
        return () => { cancelled = true; clearInterval(i); };
    }, []);

    // Filtrace pro overlay dlaždice + mapu — striktně jen auta s AKTUÁLNĚ
    // přiřazeným řidičem. Když řidič klocknul mid-rozvoz, vozidlo má
    // `aktualni_ridic_id=null` ale rozvoz v /aktivni-rozvozy stále existuje
    // se starým `rozvozce_id` → bez tohohle filtru by se ten samý řidič
    // objevil dvakrát (nové auto + stale rozvoz starého auta). Auta v depu
    // mají vlastní taby (Servis/Tankování).
    const aktivniVozidla = useMemo(() => {
        return vozidla.filter((v) => v.aktualni_ridic_id);
    }, [vozidla]);

    // Rozvozy filtrované jen na aktivní auta (tj. s řidičem) — RozvozyLayer
    // na mapě by jinak vykresloval polyline + markery i pro stale rozvozy.
    const aktivniRozvozy = useMemo(() => {
        const ids = new Set(aktivniVozidla.map((v) => v.id));
        return rozvozy.filter((r) => ids.has(r.vozidlo_id));
    }, [aktivniVozidla, rozvozy]);

    const rozvozMap = useMemo(() => {
        const m = new Map();
        for (const r of aktivniRozvozy) m.set(r.vozidlo_id, r);
        return m;
    }, [aktivniRozvozy]);

    // Glassmorphism varianta pro dlaždici plovoucí nad mapou — silnější
    // gradient + blur, ať mapa pod kartou prosvítá jako u POS VD. Base
    // cardStyle ostatních tabů (Tankování/Servis…) zůstává původní.
    const liveCardStyle = useMemo(() => isDark ? {
        background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)',
        border: '1px solid rgba(255,255,255,0.12)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)',
    } : {
        background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)',
        border: '1px solid rgba(0,0,0,0.06)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 6px 24px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.7)',
    }, [isDark]);

    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    // Karta nemá single-click akci — výběr/zoom se řídí klikem na LIVE badge
    // (cyklí přes vozidla) nebo klikem na marker. Detail otevírá dvojklik na kartu.
    // Memoizováno, aby každý LiveTab re-render nerebuiloval handlery (které pak
    // tahají MarkersLayer useEffect s sebou — onMarkerClick prop ref se mění).
    const handleOpenDetail = useCallback((id) => {
        setVybraneId(id);
        setInspectorOpen(true);
    }, [setVybraneId, setInspectorOpen]);

    const handleMarkerClick = useCallback((id) => {
        setVybraneId(id);
    }, [setVybraneId]);

    // Cyklí přes aktivní auta: null (všechna) → v[0] → v[1] → … → null.
    // Cyklíme jen ta, co jsou na mapě (s řidičem nebo aktivním rozvozem),
    // aby user neskočil na auto v depu mimo přehled.
    const cycleSelected = useCallback(() => {
        const list = aktivniVozidla;
        if (list.length === 0) return;
        if (!vybraneId) {
            setVybraneId(list[0].id);
            return;
        }
        const i = list.findIndex((v) => v.id === vybraneId);
        const next = i + 1;
        setVybraneId(next >= list.length ? null : list[next].id);
    }, [aktivniVozidla, vybraneId, setVybraneId]);

    // Klik do prázdné plochy seznamu (mimo dlaždici) = deselect → mapa znovu
    // ukáže všechna auta. Hlídáme `target === currentTarget`, aby kliky na
    // dlaždici (button) nebublaly k deselectu.
    const handleEmptyClick = (e) => {
        if (e.target === e.currentTarget) {
            setVybraneId(null);
            setInspectorOpen(false);
        }
    };

    const panelOpen = inspectorOpen && !!vybrane;

    // Vozidla overlay — desktop sloupec vlevo, mobile horizontální pás nahoře.
    // Karty jsou kompaktní (avatar + 2 chipy) na mobilu, plné na desktopu.
    const vozidlaOverlay = (
        <div
            onClick={handleEmptyClick}
            style={isMobile ? {
                // Mobile — avatary v levém dolním rohu, vertikálně, bez kontejneru.
                // Každý avatar i jeho chips si nesou vlastní pozadí.
                position: 'absolute',
                bottom: 16, left: 12,
                top: 'auto',
                zIndex: 400,
                display: 'flex', flexDirection: 'column-reverse', gap: 6,
                maxHeight: 'calc(100% - 32px)',
                overflowY: 'auto',
                background: 'transparent',
                border: 'none',
                padding: 0,
                scrollbarWidth: 'none',
            } : {
                // Desktop — žádný společný kontejner, jen scrollovatelná zóna.
                // Karty si vlastní styl drží samy (mezery mezi kartami řeší
                // marginBottom uvnitř karet).
                position: 'absolute',
                top: 12, left: 12,
                width: 340,
                maxHeight: 'calc(100% - 24px)',
                zIndex: 400,
                overflowY: 'auto',
                background: 'transparent',
                border: 'none',
                padding: 0,
            }}
        >
            {aktivniVozidla.map((v) => {
                const ridic = v.aktualni_ridic_id ? ridiciMap.get(v.aktualni_ridic_id) : null;
                const rozvoz = rozvozMap.get(v.id) || null;
                // Skóre podle aktivního řidiče — fallback na rozvozce_id z rozvozu
                // pro případ, že by ridiciMap měla zpoždění (driver Reverb event
                // ještě nedoletěl).
                const skoreKey = v.aktualni_ridic_id || rozvoz?.rozvozce_id;
                const skore = skoreKey ? ridiciSkore[skoreKey] : null;

                if (isMobile) {
                    return (
                        <VozidloDlazdice
                            key={v.id}
                            vozidlo={v}
                            ridic={ridic}
                            rozvoz={rozvoz}
                            selected={v.id === vybraneId}
                            onOpenDetail={() => handleOpenDetail(v.id)}
                            compact
                            cardStyle={liveCardStyle}
                            isDark={isDark}
                        />
                    );
                }
                // Desktop = 1:1 port POS VD karty (glass + znamka + objednavky).
                return (
                    <VozidloKartaLive
                        key={v.id}
                        vozidlo={v}
                        ridic={ridic}
                        rozvozyEntry={rozvoz}
                        skore={skore}
                        onClick={() => handleMarkerClick(v.id)}
                        onOpenDetail={() => handleOpenDetail(v.id)}
                    />
                );
            })}
            {aktivniVozidla.length === 0 && (
                <div style={{
                    padding: 12, textAlign: 'center', color: labelColor, fontSize: 12,
                    background: isDark ? 'rgba(10,6,18,0.85)' : 'rgba(255,255,255,0.95)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                    borderRadius: 9,
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                }}>
                    {vozidla.length === 0
                        ? 'Zatím žádná vozidla — přidej první.'
                        : 'Žádný řidič ve směně — auta se objeví, jakmile někdo nastoupí.'}
                </div>
            )}
        </div>
    );

    // Zóny jako tichá vrstva pod auty — vypínač vedle zvětšení mapy.
    const zonyTlacitko = zony.length > 0 ? (
        <button
            type="button"
            onClick={() => setUkazatZony((z) => !z)}
            title={ukazatZony ? 'Skrýt rozvozové zóny' : 'Ukázat rozvozové zóny'}
            aria-label={ukazatZony ? 'Skrýt rozvozové zóny' : 'Ukázat rozvozové zóny'}
            aria-pressed={ukazatZony}
            style={{
                position: 'absolute',
                bottom: isFullscreen ? 'max(22px, calc(env(safe-area-inset-bottom, 0px) + 14px))' : 22,
                right: isFullscreen ? 'max(64px, calc(env(safe-area-inset-right, 0px) + 56px))' : 64,
                zIndex: 400, width: 40, height: 40, borderRadius: '50%',
                background: ukazatZony ? SECTION_COLOR : (isDark ? 'rgba(10,6,18,0.90)' : 'rgba(255,255,255,0.95)'),
                color: ukazatZony ? '#0A0612' : (isDark ? '#fff' : 'var(--text-primary)'),
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.20)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <MapPinned size={18} strokeWidth={2.2} />
        </button>
    ) : null;

    // Toggle mezi standardním a viewport-fill režimem.
    // Není to nativní fullscreen prohlížeče (Escape+top bar by zmizely); jen
    // layout overlay přes celé okno aplikace.
    const fullscreenFab = (
        <button
            type="button"
            onClick={() => setIsFullscreen((v) => !v)}
            title={isFullscreen ? 'Zavřít zvětšení (Esc)' : 'Roztáhnout přes celé okno'}
            aria-label={isFullscreen ? 'Zavřít zvětšení' : 'Roztáhnout přes celé okno'}
            style={{
                position: 'absolute',
                bottom: isFullscreen
                    ? 'max(22px, calc(env(safe-area-inset-bottom, 0px) + 14px))'
                    : 22,
                right: isFullscreen
                    ? 'max(16px, calc(env(safe-area-inset-right, 0px) + 8px))'
                    : 16,
                zIndex: 400,
                width: 40, height: 40,
                borderRadius: '50%',
                background: isDark ? 'rgba(10,6,18,0.90)' : 'rgba(255,255,255,0.95)',
                color: isDark ? '#fff' : 'var(--text-primary)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.20)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.15s, background 0.15s',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
            {isFullscreen ? <Minimize2 size={18} strokeWidth={2.2} /> : <Maximize2 size={18} strokeWidth={2.2} />}
        </button>
    );

    const containerStyle = isFullscreen
        ? {
            position: 'fixed', inset: 0,
            zIndex: 9999,
            background: isDark ? '#0A0612' : '#f5f5f7',
            overflow: 'hidden',
            borderRadius: 0,
        }
        : {
            position: 'relative',
            marginLeft: isMobile ? -16 : 0,
            marginRight: isMobile ? -16 : 0,
            // 100dvh = dynamický viewport (počítá s URL barem mobilů). Odečítáme:
            // top bar (68) + tab bar Flotily (~56) + drobná rezerva + iOS safe
            // areas. Spodní lišta na mobilu není, mapa jde až dolů.
            height: isMobile
                ? 'calc(100dvh - 68px - 56px - 16px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))'
                : 'calc(100vh - 200px)',
            minHeight: isMobile ? 320 : 480,
            overflow: 'hidden',
            borderRadius: isMobile ? 0 : 14,
            // Vlastní vrstva: ovládání mapy má zIndex 400 (nad dlaždicemi
            // Leafletu) a bez izolace se kreslilo i přes menu modulů (70)
            // a další překryvy aplikace.
            isolation: 'isolate',
        };

    const liveContainer = (
        <div ref={isFullscreen ? null : mapaRef} style={containerStyle}>
            <div style={{ position: 'absolute', inset: 0 }}>
                <MapVozidla
                    vozidla={aktivniVozidla}
                    selectedId={vybraneId}
                    onMarkerClick={handleMarkerClick}
                    onCycleSelected={cycleSelected}
                    ridiciMap={ridiciMap}
                    selectedJizdaId={selectedJizdaId}
                    playback={playback}
                    rozvozy={aktivniRozvozy}
                    pripravuje={pripravuje}
                    cardStyle={cardStyle}
                    isDark={isDark}
                    isFullscreen={isFullscreen}
                    isMobile={isMobile}
                    zony={ukazatZony ? zony : []}
                />
            </div>
            {vozidlaOverlay}
            {fullscreenFab}
            {zonyTlacitko}
        </div>
    );

    return (
        <>
            {/* Fullscreen renderujeme přes portál do document.body, aby fixed
                kontejner spolehlivě ležel nad mobilním headerem (z-30) i bottom
                navem (z-40) — žádný ancestor s transform/contain ho neuzavře. */}
            {isFullscreen ? createPortal(liveContainer, document.body) : liveContainer}

            <SidePanel
                open={panelOpen}
                onClose={() => setInspectorOpen(false)}
                title={null}
                modal={false}
                width={500}
                isDark={isDark}
                isMobile={isMobile}
                accent={SECTION_COLOR}
                topOffset={inspectorTopOffset}
            >
                {vybrane ? (
                    <Inspector
                        vozidlo={vybrane}
                        ridic={ridicVybr}
                        onClose={() => setInspectorOpen(false)}
                        onDetailAuta={onOtevritAuto ? () => onOtevritAuto(vybrane.id) : null}
                        isDark={isDark}
                        selectedJizdaId={selectedJizdaId}
                        onSelectJizda={setSelectedJizdaId}
                        playback={playback}
                    />
                ) : null}
            </SidePanel>
        </>
    );
}
